import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";

import { get, isEmpty, isEqual, pickBy, some } from "lodash";

import { useDispatch, useSelector } from "react-redux";
import { validationErrorsSelector } from "redux-store/static/redux/selector";

import {
  changeFormErrors,
  ErrorsType,
} from "redux-store/static/redux/validation";

export type FormData = Record<string, any>;
export type ValidatorFunction = (
  value: any,
  formData: FormData
) => string | undefined;

interface ClearFieldErrorProps {
  fieldName: string;
  value?: any;
}
// Hàm validate kiểm tra không để trống
const validateRequired: ValidatorFunction = (value) => {
  return !value ? "Vui lòng không để trống" : undefined;
};

// Hàm validate kiểm tra file không  trống
const validateFileRequired: ValidatorFunction = (value) => {
  return !value ? "Vui lòng upload file khi thay đổi" : undefined;
};

// Hàm validate kiểm tra định dạng ngày
const validateDateFormat: ValidatorFunction = (value) => {
  return !moment(value).isValid() ? "Sai định dạng ngày" : undefined;
};

// Hàm validate kiểm tra định dạng email
const validateEmailFormat: ValidatorFunction = (value) => {
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return !emailPattern.test(value) ? "Địa chỉ email không hợp lệ" : undefined;
};

// Khai báo các hàm validate chung
export const commonValidators: Record<string, ValidatorFunction> = {
  required: validateRequired,
  fileRequired: validateFileRequired,
  validDate: validateDateFormat,
  validEmail: validateEmailFormat,
};

const generateErrors = (
  formData: FormData,
  schemas: Record<string, ValidatorFunction[]>
) => {
  const errors: Record<string, string> = {};

  for (const key in schemas) {
    const errorMessages = schemas[key]
      .map((validate) => validate(!isEmpty(get(formData, key)), formData))
      .filter(Boolean);

    if (errorMessages.length > 0) {
      errors[key] = errorMessages[0];
      // Chỉ lấy lỗi đầu tiên
    }
  }
  return errors;
};

const useFormValidationBase = (
  state: {
    isScrollToFirstError: boolean;
    formErrors: Record<string, string>;
  },
  setState: Function,
  formData: FormData,
  validationSchemas: Record<string, ValidatorFunction[]>,
  elementsRef?: Record<string, HTMLElement | null>
) => {
  const { formErrors, isScrollToFirstError } = state;

  const prevFormErrorsRef = useRef<Record<string, string> | null>(null);

  const handleFocusOnFirstError = useCallback(
    (errors: Record<string, string>) => {
      if (!elementsRef || Object.keys(errors).length === 0) return;

      const firstErrorKey = Object.keys(errors)[0];
      const ref = elementsRef[firstErrorKey];

      if (ref) {
        ref.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });

        // Nếu là trường nhập liệu, thực hiện focus
        if (ref instanceof HTMLInputElement) {
          ref.focus({ preventScroll: true });
        }
      }
    },
    [elementsRef]
  );

  // useEffect để xử lý khi có lỗi validate form
  useEffect(() => {
    if (
      isScrollToFirstError &&
      !isEqual(prevFormErrorsRef.current, formErrors)
    ) {
      const errors = pickBy(formErrors, (value) => value !== "");
      handleFocusOnFirstError(errors);
      prevFormErrorsRef.current = formErrors;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formErrors]);

  const validateForm = useCallback(
    (onSuccess?: () => void) => {
      const errorStore = pickBy(formErrors, (value) => value !== "");
      const errors = some(formErrors, (error) => error !== "")
        ? { ...formErrors, ...errorStore }
        : generateErrors(formData, validationSchemas);
      const isValid = Object.keys(errors).length === 0;
      if (isValid) {
        onSuccess?.();
      } else {
        setState({
          isScrollToFirstError: true,
          formErrors: errors,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formData, validationSchemas, elementsRef]
  ); //  Memo hóa các hàm validateForm, clearErrors, và clearFieldError để tránh tạo lại khi không cần thiết.

  const clearErrors = () =>
    setState({ isScrollToFirstError: false, formErrors: {} });

  const clearFieldError = useCallback((props: ClearFieldErrorProps) => {
    setState((prevErrors) => ({
      isScrollToFirstError: false,
      formErrors: { ...prevErrors.formErrors, [props.fieldName]: "" },
    }));
  }, []);

  return { validateForm, clearErrors, clearFieldError };
};

// useValidateFormData: Xử lý validate dữ liệu form
const useFormValidation = (
  formData: FormData,
  validationSchemas: Record<string, ValidatorFunction[]>,
  elementsRef?: Record<string, HTMLElement | null>
) => {
  const [state, setState] = useState<{
    isScrollToFirstError: boolean;
    formErrors: Record<string, string>;
  }>({ isScrollToFirstError: false, formErrors: {} });

  const { validateForm, clearErrors, clearFieldError } = useFormValidationBase(
    state,
    setState,
    formData,
    validationSchemas,
    elementsRef
  );

  return {
    validateForm,
    formErrors: state.formErrors,
    clearErrors,
    clearFieldError,
  };
};

// useGlobalFormValidation: Xử lý validate dữ liệu form với error được lưu trến store
// mục đích để có thể xử lý validate error với các lỗi từ BE trả về
const useGlobalFormValidation = (
  key: string,
  formData: FormData,
  validationSchemas: Record<string, ValidatorFunction[]>,
  elementsRef?: Record<string, HTMLElement | null>
) => {
  const dispatch = useDispatch();

  const state = useSelector(validationErrorsSelector(key));

  const setState = (payload: ErrorsType) => {
    dispatch(changeFormErrors({ key, payload }));
  };

  const { validateForm, clearErrors, clearFieldError } = useFormValidationBase(
    state,
    setState,
    formData,
    validationSchemas,
    elementsRef
  );

  return {
    validateForm,
    formErrors: state.formErrors,
    clearErrors,
    clearFieldError,
  };
};

export { useFormValidation, useGlobalFormValidation };
